// Post-first-coin Watchlist onboarding: "What do you want to watch next?"
//
// Coin result → here → Home. Also offered once on the next fresh load of Home if
// the step was left unfinished. Every tap goes straight to the existing default
// Watchlist API (nothing is batched locally) and no failure here can keep the
// user from Home. Browser back = Skip for now → Home (never the finished quiz).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { OnboardingService } from '../../services/onboardingService';
import { WatchlistService } from '../../services/watchlistService';
import { MediaService } from '../../services/mediaService';
import Modal from '../../components/Modal/Modal';
import CastSection from '../../components/CastSection/CastSection';
import { reportActivationEvent } from '../../services/authService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './WatchlistOnboarding.module.css';

const SOURCE = 'watchlist_onboarding';
const TOAST_MS = 2600;
const ERROR_ADD = "Couldn't add that right now. Try again.";
const ERROR_REMOVE = "Couldn't remove that right now. Try again.";

const keyOf = (t) => `${t.kind}:${t.tmdbId}`;

function PosterCard({ item, state, onToggle, onAbout }) {
    const added = state === 'added' || state === 'removing';
    const busy = state === 'adding' || state === 'removing';
    const meta = [item.year, item.kind === 'tv' ? 'TV' : 'Movie'].filter(Boolean).join(' · ');

    return (
        <li className={styles.cardItem}>
            <button
                type="button"
                className={`${styles.card} ${added ? styles.cardAdded : ''}`}
                onClick={onToggle}
                disabled={busy}
                aria-pressed={added}
                aria-label={`${item.title}${added ? ', added to Watchlist' : ''}`}
            >
                <span className={styles.posterWrap}>
                    <img className={styles.poster} src={item.posterUrl} alt="" loading="lazy" />
                    {added && <span className={styles.addedBadge} aria-hidden="true">✓</span>}
                </span>
                <span className={styles.cardTitle}>{item.title}</span>
                {meta && <span className={styles.cardMeta}>{meta}</span>}
                <span className={`${styles.pill} ${added ? styles.pillAdded : ''}`} aria-hidden="true">
                    {busy ? <span className={styles.spinner} /> : added ? '✓ Added' : '+ Watchlist'}
                </span>
            </button>
            {/* Sibling of the card button (buttons can't nest), laid over the poster corner. */}
            <button type="button" className={styles.aboutBtn} onClick={onAbout} aria-label={`About ${item.title}`}>
                i
            </button>
        </li>
    );
}

// "What's it about?" — poster, synopsis and cast for one suggestion, with the
// same Watchlist toggle as its card. The card's data shows immediately; the
// synopsis and cast arrive from the media details endpoint.
function AboutModal({ item, state, onToggle, onClose }) {
    const [details, setDetails] = useState(null);
    const [status, setStatus] = useState('loading');
    const tmdbId = item?.tmdbId;
    const kind = item?.kind;

    useEffect(() => {
        if (!tmdbId) return undefined;
        let cancelled = false;
        setDetails(null);
        setStatus('loading');
        MediaService.getDetails(kind, tmdbId)
            .then((data) => { if (!cancelled) { setDetails(data); setStatus('ready'); } })
            .catch(() => { if (!cancelled) setStatus('failed'); });
        return () => { cancelled = true; };
    }, [kind, tmdbId]);

    if (!item) return null;
    const added = state === 'added' || state === 'removing';
    const busy = state === 'adding' || state === 'removing';
    const meta = [item.year, item.kind === 'tv' ? 'TV' : 'Movie'].filter(Boolean).join(' · ');

    return (
        <Modal isOpen onClose={onClose}>
            <div className={styles.about}>
                <div className={styles.aboutTop}>
                    <img className={styles.aboutPoster} src={item.posterUrl} alt="" />
                    <div className={styles.aboutHeading}>
                        <h2 className={styles.aboutTitle}>{item.title}</h2>
                        {meta && <p className={styles.cardMeta}>{meta}</p>}
                        <button
                            type="button"
                            className={`${styles.pill} ${styles.aboutPill} ${added ? styles.pillAdded : ''}`}
                            onClick={onToggle}
                            disabled={busy}
                            aria-pressed={added}
                        >
                            {busy ? <span className={styles.spinner} /> : added ? '✓ Added' : '+ Watchlist'}
                        </button>
                    </div>
                </div>
                {status === 'loading' && (
                    <div className={styles.aboutStatus}><span className={styles.spinnerLarge} aria-label="Loading details" /></div>
                )}
                {status === 'failed' && <p className={styles.aboutStatus}>We couldn't load the details right now.</p>}
                {status === 'ready' && (
                    <>
                        <p className={styles.aboutOverview}>{details?.overview || 'No synopsis available yet.'}</p>
                        <CastSection cast={details?.cast} media={item} kind={item.kind} />
                    </>
                )}
            </div>
        </Modal>
    );
}

export default function WatchlistOnboarding() {
    const navigate = useNavigate();
    const { state: navState } = useLocation();
    const { user, resolveWatchlistOnboarding } = useAuth();

    const entry = navState?.entry ?? 'first_coin';
    const exclude = useMemo(() => (
        navState?.excludeTmdbId && navState?.excludeKind
            ? [{ tmdbId: navState.excludeTmdbId, kind: navState.excludeKind }]
            : []
    ), [navState?.excludeTmdbId, navState?.excludeKind]);

    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // key → { state: 'adding' | 'added' | 'removing', itemId }
    const [selection, setSelection] = useState({});
    const [toast, setToast] = useState(null);
    const [aboutIndex, setAboutIndex] = useState(null);

    const selectionRef = useRef({});
    const inFlightRef = useRef(new Set());
    const resolvedRef = useRef(false);
    const startedRef = useRef(false);
    const firstAddShownRef = useRef(false);
    const toastTimerRef = useRef(null);
    const pageRef = useRef(1);
    const loadingMoreRef = useRef(false);
    const sentinelRef = useRef(null);

    const setItem = useCallback((key, value) => {
        const next = { ...selectionRef.current };
        if (value) next[key] = value; else delete next[key];
        selectionRef.current = next;
        setSelection(next);
    }, []);

    const showToast = useCallback((text, tone = 'info') => {
        clearTimeout(toastTimerRef.current);
        setToast({ text, tone });
        toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    const loadSuggestions = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);
        pageRef.current = 1;
        try {
            const data = await OnboardingService.getWatchlistSuggestions(exclude);
            setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
            setHasMore(data?.hasMore === true);
        } catch {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [exclude]);

    useEffect(() => {
        loadSuggestions();
    }, [loadSuggestions]);

    // Infinite scroll: more titles are a bonus, so any failure just ends the list.
    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || resolvedRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const nextPage = pageRef.current + 1;
            const data = await OnboardingService.getWatchlistSuggestions(exclude, nextPage);
            const more = Array.isArray(data?.suggestions) ? data.suggestions : [];
            pageRef.current = nextPage;
            setSuggestions((prev) => {
                const shown = new Set(prev.map(keyOf));
                return [...prev, ...more.filter((t) => !shown.has(keyOf(t)) && shown.add(keyOf(t)))];
            });
            setHasMore(data?.hasMore === true);
        } catch {
            setHasMore(false);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [exclude]);

    // Re-observed after every page so a sentinel that is still on screen (a
    // page that was mostly duplicates) keeps loading.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || loadingMore || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) loadMore();
        }, { rootMargin: '400px 0px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loadMore, suggestions.length]);

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.WATCHLIST_ONBOARDING_STARTED, { platform: 'web', source: entry },
            { onceKey: `watchlist-onboarding-${user?.id ?? 'anon'}` });
        reportActivationEvent('watchlist_onboarding_started', { source_screen: entry });
    }, [entry, user?.id]);

    const addedCount = useMemo(
        () => Object.values(selection).filter((s) => s.state === 'added' || s.state === 'removing').length,
        [selection],
    );

    const handleToggle = useCallback(async (item, index) => {
        const key = keyOf(item);
        if (inFlightRef.current.has(key) || resolvedRef.current) return;
        inFlightRef.current.add(key);
        const current = selectionRef.current[key];

        try {
            if (!current) {
                setItem(key, { state: 'adding', itemId: null });
                let itemId = null;
                try {
                    const created = await WatchlistService.addToWatchlist(item.tmdbId, item.kind, {
                        source: SOURCE,
                        position: index + 1,
                    });
                    itemId = created?.data?.id ?? created?.id ?? null;
                } catch (err) {
                    // Already on the Watchlist (added elsewhere) is a success for the user.
                    if (err?.statusCode !== 409) throw err;
                }
                setItem(key, { state: 'added', itemId });
                trackEvent(ANALYTICS_EVENTS.WATCHLIST_ONBOARDING_ITEM_ADDED, {
                    media_type: item.kind,
                    media_id: item.tmdbId,
                    source: SOURCE,
                    position: index + 1,
                }, { onceKey: `${user?.id ?? 'anon'}-${key}` });
                if (!firstAddShownRef.current) {
                    firstAddShownRef.current = true;
                    showToast('Added to your Watchlist');
                }
            } else if (current.state === 'added') {
                setItem(key, { ...current, state: 'removing' });
                let itemId = current.itemId;
                if (!itemId) {
                    const data = await WatchlistService.getWatchlist();
                    const items = data?.data?.items ?? data?.items ?? [];
                    itemId = items.find(
                        (i) => Number(i.media?.tmdb_id) === Number(item.tmdbId) && i.media?.kind === item.kind,
                    )?.id ?? null;
                }
                if (itemId) {
                    try {
                        await WatchlistService.removeFromWatchlist(itemId);
                    } catch (err) {
                        if (err?.statusCode !== 404) throw err; // already gone is fine
                    }
                }
                setItem(key, null);
                trackEvent(ANALYTICS_EVENTS.WATCHLIST_ONBOARDING_ITEM_REMOVED, {
                    media_type: item.kind,
                    media_id: item.tmdbId,
                    source: SOURCE,
                    position: index + 1,
                });
            }
        } catch {
            setItem(key, current ?? null);
            showToast(current ? ERROR_REMOVE : ERROR_ADD, 'error');
        } finally {
            inFlightRef.current.delete(key);
        }
    }, [setItem, showToast, user?.id]);

    const finish = useCallback(({ skipped }) => {
        if (resolvedRef.current) return;
        resolvedRef.current = true;
        const itemsAddedCount = Object.values(selectionRef.current)
            .filter((s) => s.state === 'added' || s.state === 'removing').length;

        trackEvent(
            skipped ? ANALYTICS_EVENTS.WATCHLIST_ONBOARDING_SKIPPED : ANALYTICS_EVENTS.WATCHLIST_ONBOARDING_COMPLETED,
            { items_added_count: itemsAddedCount, source: SOURCE },
            { onceKey: `watchlist-onboarding-done-${user?.id ?? 'anon'}` },
        );
        // Fire-and-forget: Home must never wait on (or fail because of) this.
        resolveWatchlistOnboarding({ skipped, itemsAddedCount });
        navigate('/', { replace: true });
    }, [navigate, resolveWatchlistOnboarding, user?.id]);

    // Browser back = Skip for now → Home. Replacing the popped entry means the
    // finished quiz is never shown again from here.
    useEffect(() => {
        const onPopState = () => finish({ skipped: true });
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [finish]);

    const aboutItem = aboutIndex === null ? null : suggestions[aboutIndex] ?? null;
    const closeAbout = useCallback(() => setAboutIndex(null), []);

    let body;
    if (loading) {
        body = <div className={styles.status}><span className={styles.spinnerLarge} aria-label="Loading suggestions" /></div>;
    } else if (loadFailed || suggestions.length === 0) {
        body = (
            <div className={styles.status}>
                <p>
                    {loadFailed
                        ? "We couldn't load suggestions right now."
                        : 'No suggestions right now — you can add titles to your Watchlist anytime.'}
                </p>
                {loadFailed && (
                    <button type="button" className={styles.retryBtn} onClick={loadSuggestions}>Try again</button>
                )}
            </div>
        );
    } else {
        body = (
            <>
                <ul className={styles.grid}>
                    {suggestions.map((item, index) => (
                        <PosterCard
                            key={keyOf(item)}
                            item={item}
                            state={selection[keyOf(item)]?.state}
                            onToggle={() => handleToggle(item, index)}
                            onAbout={() => setAboutIndex(index)}
                        />
                    ))}
                </ul>
                {hasMore && (
                    <div ref={sentinelRef} className={styles.loadMore}>
                        {loadingMore && <span className={styles.spinnerLarge} aria-label="Loading more titles" />}
                    </div>
                )}
            </>
        );
    }

    return (
        <div className={styles.page}>
            <main className={styles.inner}>
                <header className={styles.header}>
                    <h1 className={styles.title}>What do you want to watch next?</h1>
                    <p className={styles.subtitle}>
                        Save a few movies or shows to your Watchlist so you always know what’s next.
                    </p>
                    <p className={styles.helper}>Tap anything you want to watch later.</p>
                </header>
                {body}
            </main>

            {aboutItem && (
                <AboutModal
                    item={aboutItem}
                    state={selection[keyOf(aboutItem)]?.state}
                    onToggle={() => handleToggle(aboutItem, aboutIndex)}
                    onClose={closeAbout}
                />
            )}

            <div className={styles.toastRegion} aria-live="polite">
                {toast && (
                    <div className={`${styles.toast} ${toast.tone === 'error' ? styles.toastError : ''}`}>{toast.text}</div>
                )}
            </div>

            <footer className={styles.footer}>
                <div className={styles.footerInner}>
                    {addedCount > 0 && (
                        <p className={styles.footerHint}>
                            {addedCount === 1 ? '1 title saved' : `${addedCount} titles saved`}
                        </p>
                    )}
                    <button type="button" className={styles.ctaBtn} onClick={() => finish({ skipped: false })}>
                        Continue
                    </button>
                    <button type="button" className={styles.skipBtn} onClick={() => finish({ skipped: true })}>
                        Skip for now
                    </button>
                </div>
            </footer>
        </div>
    );
}
